
export interface Usuario {
  id: number;
  nome: string;
  email: string | null;
  ativo: boolean | null;
  operador: boolean | null;
  alterado_em: string | null;
  colaborador_id: number | null;
  regime_contratacao_id: string | null;
  regime_contratacao_descricao: string | null;
  perfil_padrao: string | null;
  /** WhatsApp só com dígitos, DDI + DDD (ex.: 5547999999999). Não vem da API TOTVS. */
  whatsapp?: string | null;
  /** 'externo' = sincronizado do portal Prospera; 'local' = criado aqui, senha no Supabase Auth. */
  origem?: 'externo' | 'local';
  created_at: string;
  updated_at: string;
}

export interface UsuarioPermissao {
  id: string;
  usuario_id: number;
  pagina: string;
  acesso: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApiUsuario {
  id: number;
  nome: string;
  email: string;
  ativo: boolean;
  operador: boolean;
  alterado: {
    real: string;
  };
  colaborador: {
    id: number;
    regimeContratacao: {
      id: string;
      descricao: string;
    };
  };
}

export interface UsuarioFormData {
  nome: string;
  email: string;
  ativo: boolean;
  operador: boolean;
  perfil_padrao: string;
  whatsapp?: string;
}

/** Agendamento (task_agenda) que o usuário pode assinar para receber por WhatsApp. */
export interface AgendaNotificavel {
  id: string;
  titulo: string;
  tipo: string;
  ativo: boolean | null;
  descricao: string | null;
}

/** Tipos de rotina do agendamento que hoje mandam mensagem por WhatsApp. */
export const AGENDA_TIPOS_COM_WHATSAPP: readonly string[] = ['monitor_sites_fiscais'];

export const agendaEnviaWhatsapp = (tipo: string | null | undefined): boolean =>
  !!tipo && AGENDA_TIPOS_COM_WHATSAPP.includes(tipo);

/**
 * Deixa o WhatsApp só com dígitos. Com 10 ou 11 dígitos (DDD + número, sem DDI), põe o 55 do Brasil na frente.
 */
export const normalizarWhatsapp = (valor: string | null | undefined): string => {
  const digitos = (valor || '').replace(/\D/g, '');
  if (digitos.length === 10 || digitos.length === 11) return `55${digitos}`;
  return digitos;
};

/** Vazio (sem WhatsApp) ou de 10 a 15 dígitos, igual ao CHECK de usuarios.whatsapp. */
export const whatsappValido = (digitos: string): boolean => digitos === '' || /^\d{10,15}$/.test(digitos);

/** Mostra o número como +55 (47) 99999-9999; fora do padrão brasileiro, só +dígitos. */
export const formatarWhatsapp = (valor: string | null | undefined): string => {
  const d = (valor || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('55') && (d.length === 12 || d.length === 13)) {
    const ddd = d.slice(2, 4);
    const numero = d.slice(4);
    const corte = numero.length - 4;
    return `+55 (${ddd}) ${numero.slice(0, corte)}-${numero.slice(corte)}`;
  }
  return `+${d}`;
};

/**
 * Grupos exibidos na tela de permissões do usuário: só as telas que ainda estão no menu (Cadastros e Tributação).
 * `PAGINAS_SISTEMA` é o flatten desta lista — única fonte para não sumir chaves na UI.
 */
export const GRUPOS_PERMISSOES_USUARIO: { titulo: string; descricao?: string; paginas: readonly string[] }[] = [
  {
    titulo: 'Cadastros e configuração',
    paginas: ['Clientes', 'Funcionários', 'Usuários', 'Agendamento', 'Layout', 'IA - Prompts'],
  },
  {
    titulo: 'Tributação',
    descricao: "Cada tela vale sozinha. A chave 'Tributação' libera só o Passo a passo, o Painel fiscal e a Regra de custo.",
    paginas: ['Tributação', 'Tributação - NCM ICMS', 'Tributação - Perfis', 'Tributação - Regras Cálculo', 'Tributação - Regras Financeiras', 'Tributação - Tokens API', 'Tributação - Base de Conhecimento', 'Tributação - Confronto Fiscal', 'Tributação - Perfil Fiscal', 'Tributação - Diagnóstico Protheus', 'Tributação - SPED'],
  },
];

export const PAGINAS_SISTEMA = GRUPOS_PERMISSOES_USUARIO.flatMap((g) => [...g.paginas]) as readonly string[];

/** Ordem do dropdown. Admin: todas as telas e clientes. Consultor: telas marcadas, todos os clientes. Restrito: telas marcadas, só os clientes vinculados. */
export const PERFIS_USUARIO = [
  { value: 'admin', label: 'Administrador' },
  { value: 'consultor', label: 'Consultor' },
  { value: 'restrito', label: 'Restrito' }
] as const;

export const rotuloPerfil = (perfil: string | null | undefined): string =>
  PERFIS_USUARIO.find((p) => p.value === perfil?.toLowerCase())?.label ?? 'Restrito';
